import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Combo, Input, Modal, Subheading } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

const Label = styled(Subheading)`
	line-height: 34px;
`;

function JobPositionTitleField(props: {
	value: { custom: string } | { value: string } | undefined;
	onChange: (value: { custom: string } | { value: string } | undefined) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [fetchJobTitles, jobTitles] = graphql.useJobTitlesLazyQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	return (
		<Flex>
			<Box flexGrow={1} flexShrink={1} flexBasis="40%">
				<Label>{t('settings.employees.jobPositions.create.labels.title')}:</Label>
			</Box>
			<Box flexGrow={1} flexShrink={1} flexBasis="60%">
				<Combo
					autoFocus
					fetch={fetchJobTitles}
					choice={props.value}
					onChange={props.onChange}
					options={
						jobTitles.data &&
						_.sortBy(
							jobTitles.data.jobTitles.edges.map(({ node }) => ({
								title: localize(node.title).text,
								value: node.id,
							})),
							'title',
						)
					}
					explanation={t('settings.employees.jobPositions.create.custom.title')}
				/>
			</Box>
		</Flex>
	);
}

function JobPositionLevelField(props: {
	value: { custom: string } | { value: string } | undefined;
	onChange: (value: { custom: string } | { value: string } | undefined) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [fetchJobLevels, jobLevels] = graphql.useJobLevelsLazyQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	return (
		<Flex mt={2}>
			<Box flexGrow={1} flexShrink={1} flexBasis="40%">
				<Label>{t('settings.employees.jobPositions.create.labels.level')}:</Label>
			</Box>
			<Box flexGrow={1} flexShrink={1} flexBasis="60%">
				<Combo
					fetch={fetchJobLevels}
					choice={props.value}
					onChange={props.onChange}
					placeholder={t('settings.employees.jobs.create.level.placeholder')}
					options={
						jobLevels.data &&
						_.sortBy(
							jobLevels.data.jobLevels.edges.map(({ node }) => ({
								title: localize(node.title).text,
								value: node.id,
							})),
							'title',
						)
					}
					explanation={t('settings.employees.jobPositions.create.custom.level')}
				/>
			</Box>
		</Flex>
	);
}

function JobPositionStartField(props: { value: Date; onChange: (value: Date) => void }) {
	const { t } = useTranslation();

	return (
		<Flex mt={2}>
			<Box flexGrow={1} flexShrink={1} flexBasis="40%">
				<Label>{t('settings.employees.jobPositions.create.labels.startDate')}:</Label>
			</Box>
			<Box flexGrow={1} flexShrink={1} flexBasis="60%">
				<Input.DatePicker
					value={props.value}
					onChange={(value) => props.onChange(value ?? new Date())}
				/>
			</Box>
		</Flex>
	);
}

export default function CreateJobPosition(props: {
	user: graphql.User;
	onCancel: () => void;
	onDismiss: () => void;
	onCreate: () => Promise<void>;
}) {
	const { t } = useTranslation();

	const { language } = useLocalize();

	const [create] = graphql.useCreateJobPositionMutation();
	const [createJobTitle] = graphql.useCreateJobTitleMutation();
	const [createJobLevel] = graphql.useCreateJobLevelMutation();

	const [jobTitle, setJobTitle] = useState<{ custom: string } | { value: string } | undefined>(
		undefined,
	);

	const [jobLevel, setJobLevel] = useState<{ custom: string } | { value: string } | undefined>(
		undefined,
	);

	const [startDate, setStartDate] = useState(new Date());

	async function createJobTitleIfNecessary(jobTitle: { custom: string } | { value: string }) {
		if ('value' in jobTitle) {
			return jobTitle.value;
		}

		const response = await createJobTitle({
			variables: { input: { title: [{ languageID: language.id, text: jobTitle.custom }] } },
		});

		return response.data!.createJobTitle.jobTitle.id;
	}

	async function createJobLevelIfNecessary(jobLevel: { custom: string } | { value: string }) {
		if ('value' in jobLevel) {
			return jobLevel.value;
		}

		const response = await createJobLevel({
			variables: { input: { title: [{ languageID: language.id, text: jobLevel.custom }] } },
		});

		return response.data!.createJobLevel.jobLevel.id;
	}

	return (
		<Modal width={400} height="auto">
			<Modal.Header title={t('settings.employees.jobPositions.create.title')} />
			<Modal.Contents>
				<JobPositionTitleField value={jobTitle} onChange={setJobTitle} />
				{jobTitle && <JobPositionLevelField value={jobLevel} onChange={setJobLevel} />}
				{jobTitle && <JobPositionStartField value={startDate} onChange={setStartDate} />}
			</Modal.Contents>
			<Modal.FormFooter
				disabled={!jobTitle}
				onCancel={props.onCancel}
				onSave={async () => {
					const jobTitleID = await createJobTitleIfNecessary(jobTitle!);
					const jobLevelID = jobLevel && (await createJobLevelIfNecessary(jobLevel!));

					await create({
						variables: {
							input: {
								jobTitleID,
								jobLevelID,
								startDate,
								userID: props.user.id,
							},
						},
					});

					await props.onCreate();
					props.onDismiss();
				}}
			/>
		</Modal>
	);
}
